// Icon — the one glyph registry (inevitability P33 / CC2).
//
// Before it, every glyph was a hand-drawn inline `<svg>`, so a concept like
// "close" could not have ONE form (I2-for-glyphs: one meaning ↔ one form).
// This module is that home: a CLOSED named set (`IconName`), one weight (2px
// stroke on a 24-grid), `currentColor` so the glyph inherits text colour, and
// a token-driven scale. The dot/ellipsis collision (CC1) is resolved here:
// `overflow` ("…") is the ONE row-overflow glyph, `dot` the ONE status dot.
//
// Every name maps, in one line, to a lucide-react component (pinned version,
// so an upgrade is deliberate). Never hand-draw or copy SVG paths: a new
// concept earns a new name here (or in its lane's `glyphs/<lane>.tsx`). The one
// non-Lucide glyph is `dot`, a solid status mark Lucide has no equivalent for.
// Lucide is ISC-licensed (with Feather's MIT for some glyphs); the notice is
// kept in the adjacent `LICENSE` file.
//
// Pure/props-driven. Decorative by default (`aria-hidden`), because an
// icon almost always sits beside its own label (a Button's text, an
// IconButton's required `label`). Pass `title` ONLY when the icon is the
// sole carrier of meaning — it then becomes an `img` with an accessible
// name. Never both a visible label and a `title` (that double-announces).

export type { AreaGlyphs, IconName, IconProps, IconSize };
export { GLYPHS, Icon };

import {
    Anchor,
    Archive,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    ArrowUpDown,
    ArrowUpRight,
    AudioLines,
    Bold,
    BookOpen,
    Boxes,
    Brain,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronsDown,
    ChevronsUpDown,
    ChevronUp,
    CircleAlert,
    CircleQuestionMark,
    CircleStop,
    CircleX,
    Clock,
    Code,
    Copy,
    CornerDownRight,
    createLucideIcon,
    Database,
    Download,
    Ellipsis,
    ExternalLink,
    EyeOff,
    FileIcon,
    FileImage,
    FilePlus,
    FileQuestionMark,
    FileText,
    FileUp,
    Folder,
    FolderOpen,
    Gauge,
    Globe,
    GraduationCap,
    GripVertical,
    Hand,
    Heading,
    House,
    ImageIcon,
    Info,
    Italic,
    LayoutDashboard,
    LayoutGrid,
    Library,
    LinkIcon,
    ListChecks,
    LoaderCircle,
    LogOut,
    type LucideIcon,
    Maximize2,
    Menu,
    MessageCircle,
    MessageSquareReply,
    MessagesSquare,
    Minus,
    Moon,
    MousePointer2,
    OctagonAlert,
    Package,
    PanelLeft,
    PanelLeftClose,
    PanelLeftOpen,
    PanelRight,
    Paperclip,
    Pencil,
    PencilLine,
    Pilcrow,
    Pin,
    Play,
    Plus,
    Presentation,
    Redo2,
    RefreshCw,
    Rows2,
    Rows3,
    Scan,
    Search,
    Settings,
    ShieldCheck,
    SkipForward,
    SlidersHorizontal,
    Sparkles,
    Square,
    SquareCode,
    SquarePen,
    Star,
    StickyNote,
    Sun,
    Target,
    Terminal,
    Trash2,
    TriangleAlert,
    Undo2,
    Unlink,
    Upload,
    User,
    Video,
    WifiOff,
    X,
} from "lucide-react";
import "./Icon.css";
import { chatGlyphs } from "./glyphs/chat";
import { detailGlyphs } from "./glyphs/detail";
import { feedbackGlyphs } from "./glyphs/feedback";
import { libraryGlyphs } from "./glyphs/library";
import { opsGlyphs } from "./glyphs/ops";
import { projectsGlyphs } from "./glyphs/projects";
import { studioGlyphs } from "./glyphs/studio";

type IconSize = "xs" | "sm" | "md" | "lg";

type IconProps = {
    readonly name: IconName;
    // Maps to the --icon-xs (12) / --icon-sm (16) / --icon-md (20) / --icon-lg
    // (24) tokens. Default sm — the app's dense default; a caller sizes up
    // deliberately. `xs` is for dense decorative inline glyphs (chip/tab/
    // provenance) and NEVER for a control's sole hit target; `lg` is for media-
    // frame and banner status glyphs.
    readonly size?: IconSize;
    // Present ONLY when the icon carries meaning with no adjacent text.
    // Absent → decorative (aria-hidden), the common case.
    readonly title?: string;
    readonly className?: string;
};

// The one status dot: solid, so it fills instead of stroking.
const Dot = createLucideIcon("dot", [
    [
        "circle",
        {
            cx: "12",
            cy: "12",
            r: "4",
            fill: "currentColor",
            stroke: "none",
            key: "dot",
        },
    ],
]);

// The shared names, grouped by role so the vocabulary reads as a system. A
// glyph only one area uses goes in that area's `glyphs/<lane>.tsx`
// (docs/implement/lanes.md).
const BASE_GLYPHS = {
    // structure / navigation
    "chevron-right": ChevronRight,
    "chevron-down": ChevronDown,
    "chevron-left": ChevronLeft,
    "chevron-up": ChevronUp,
    "chevrons-down": ChevronsDown, // minimise a panel (the chat panel's collapse)
    "chevrons-up-down": ChevronsUpDown, // a switcher that opens a list (the institution)
    "arrow-right": ArrowRight,
    "arrow-left": ArrowLeft, // back to where it came from (a breadcrumb)
    "arrow-up-right": ArrowUpRight, // open it where it lives
    "arrow-up": ArrowUp, // send (composer submit); move a part up
    "arrow-down": ArrowDown, // move a part down
    grip: GripVertical, // a drag handle (reorder a part)
    menu: Menu, // hamburger — reveal the collapsed rail
    expand: Maximize2, // diagonal arrows — grow a pane
    panel: PanelRight, // dock as a side panel
    "panel-left": PanelLeft, // show or hide the app's menu (the Studio toolbar)
    "panel-left-close": PanelLeftClose, // fold the menu into its icon rail
    "panel-left-open": PanelLeftOpen, // unfold the menu (or keep the peek open)
    "wifi-off": WifiOff, // the server (or the network) can't be reached
    refresh: RefreshCw, // try again now
    home: House, // a project's landing surface (pinned tab)
    // actions
    close: X,
    check: Check,
    plus: Plus,
    minus: Minus,
    search: Search,
    "external-link": ExternalLink,
    trash: Trash2, // destructive remove
    copy: Copy, // duplicate text to the clipboard
    pencil: Pencil, // edit / author
    compose: SquarePen, // start a NEW chat (distinct from "+" = add file)
    gear: Settings, // settings
    "sign-out": LogOut, // leave the session
    undo: Undo2,
    redo: Redo2,
    paperclip: Paperclip, // attach
    download: Download, // export to disk
    upload: Upload, // bring a file in
    command: Terminal, // a runnable app command (prompt chevron)
    play: Play, // start media
    help: CircleQuestionMark, // a HelpHint affordance
    // domain content kinds + navigation objects: one form per kind, named by
    // the CONCEPT it carries, so the kind ↔ glyph bijection (I2) is enforced
    // by the name, not convention.
    workspace: LayoutGrid, // the workspace switcher's place-of-rooms grid
    library: Library, // the Library — sources/materials/packages
    folder: Folder, // a project
    "folder-open": FolderOpen, // a project with no fixed form; an unfolded module
    canvas: LayoutDashboard, // the Studio — cards laid out on a canvas
    knowledge: Brain, // the Knowledge bank — what the assistant knows
    module: Boxes, // a module: parts put together
    course: GraduationCap, // a course: modules put together
    pointer: MousePointer2, // the select tool
    hand: Hand, // the pan tool
    scan: Scan, // fit a canvas to its content (not a curved arrow: that reads as undo)
    reorder: ArrowUpDown, // change the order of a container's parts
    "eye-off": EyeOff, // take off a view without deleting it (remove from the canvas)
    source: FileIcon, // a source file (raw input)
    sources: BookOpen, // a reply's references
    "file-up": FileUp, // bring a source onto the canvas (the Studio toolbar)
    "file-plus": FilePlus, // a new material on the canvas (the Studio toolbar)
    material: FileText, // an authored learning material
    export: Package, // a produced, shippable export
    file: FileImage, // an opaque asset file (image, audio, …), not yet a source
    document: FileText, // a text document (PDF, Word, plain text)
    presentation: Presentation, // a slide deck
    "unknown-file": FileQuestionMark, // a file of a type the app can't show
    person: User, // the account identity avatar
    chat: MessageCircle, // the Assistant / a conversation
    sparkle: Sparkles, // AI generation
    sliders: SlidersHorizontal, // preferences
    database: Database, // stored data
    clock: Clock, // time / recency
    anchor: Anchor, // grounding — a claim tied to a source
    star: Star, // rating / provenance highlight
    shield: ShieldCheck, // a verified / safe plan
    pin: Pin, // fasten a chat/scope to one resource
    archive: Archive, // set aside — the Library's archived scope
    "rows-3": Rows3, // the Compact list density
    "rows-2": Rows2, // the Detailed list density
    // appearance (the theme quick-toggle — one glyph per palette)
    sun: Sun, // light theme
    moon: Moon, // dark theme
    globe: Globe, // interface language (the language quick-picker)
    // editor content-block kinds (the Palette insert vocabulary)
    heading: Heading,
    paragraph: Pilcrow,
    objective: Target,
    note: StickyNote, // a Note: for the author and the assistant only
    mcq: ListChecks,
    image: ImageIcon,
    video: Video,
    audio: AudioLines,
    embed: SquareCode,
    // editor inline formatting marks
    bold: Bold,
    italic: Italic,
    code: Code,
    link: LinkIcon,
    unlink: Unlink, // a broken chain — no source tie (ungrounded)
    // overflow + status (the CC1 dot resolution lives here)
    overflow: Ellipsis, // "…" — the ONE row-overflow glyph
    dot: Dot, // the ONE status dot
    info: Info,
    warning: TriangleAlert, // a caution to attend to
    danger: OctagonAlert, // a failure that blocks (the ONE danger form)
    error: CircleX, // a generic error mark
    stopped: CircleStop,
    // chat thread (pen "Chat – every entry")
    loading: LoaderCircle, // a step still running
    stop: Square, // the reply was stopped
    alert: CircleAlert, // not sent / went wrong
    "conversation-long": MessagesSquare, // the conversation outgrew the assistant's memory
    budget: Gauge,
    answered: MessageSquareReply, // you answered the assistant's questions
    skipped: SkipForward, // you skipped them
    "answer-line": CornerDownRight, // one question and its answer
    "own-answer": PencilLine, // write your own answer
} satisfies Record<string, LucideIcon>;

type BaseIconName = keyof typeof BASE_GLYPHS;

// An area's own glyphs: new names only (a shared name fails to type-check).
type AreaGlyphs = { readonly [K in BaseIconName]?: never } & Record<
    string,
    LucideIcon
>;

type IconName =
    | BaseIconName
    | keyof typeof studioGlyphs
    | keyof typeof chatGlyphs
    | keyof typeof projectsGlyphs
    | keyof typeof libraryGlyphs
    | keyof typeof detailGlyphs
    | keyof typeof feedbackGlyphs
    | keyof typeof opsGlyphs;

const GLYPHS: Record<IconName, LucideIcon> = {
    ...BASE_GLYPHS,
    ...studioGlyphs,
    ...chatGlyphs,
    ...projectsGlyphs,
    ...libraryGlyphs,
    ...detailGlyphs,
    ...feedbackGlyphs,
    ...opsGlyphs,
};

const Icon = ({ name, size = "sm", title, className }: IconProps) => {
    const Glyph = GLYPHS[name];
    const decorative = title === undefined;
    return (
        <Glyph
            className={`bilda-icon bilda-icon--${size}${className ? ` ${className}` : ""}`}
            role={decorative ? undefined : "img"}
            aria-hidden={decorative ? true : undefined}
            aria-label={title}
        >
            {title !== undefined ? <title>{title}</title> : null}
        </Glyph>
    );
};
